import jwt_decode from 'jwt-decode';
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useEffect, useReducer } from 'react';
import { removeTokenInfo } from 'src/api';
import { auto_login } from 'src/api/auth';
import { authConfig } from 'src/config';
import { useRouter } from 'src/hooks/use-router';
import { useSnackBar } from 'src/hooks/use-snackbars';
import { paths } from 'src/paths';
import {
  ACTION_TYPE_ENUM,
  AccessToken,
  Action,
  AutoLogin,
  Handler,
  InitializeAction,
  SignInAction,
  State,
  UserInfo
} from 'src/types/auth';
import { STORAGE_ACTION } from 'src/utils/storageAction';

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  userInfo: null
};

const handlers: Record<ACTION_TYPE_ENUM, Handler> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const payload = action.payload;
    return {
      ...state,
      isInitialized: true,
      ...payload
    };
  },
  SIGN_IN: (state: State, action: SignInAction): State => {
    const payload = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      ...payload
    };
  },
  SIGN_OUT: (state: State): State => ({
    ...state,
    isAuthenticated: false
  }),
  SIGN_UP: (state: State): State => ({
    ...state,
    isAuthenticated: false
  })
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export interface AuthContextType extends State {
  signIn: (capaAccessToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  // signUp: (body: RegisterInput, friendsType: USER_TYPE_ENUM) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve()
  // signUp: () => Promise.resolve()
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { handleSnackBar } = useSnackBar();

  const initialize = useCallback(async (): Promise<void> => {
    try {
      const accessToken = globalThis.localStorage.getItem(
        authConfig.accessTokenKey!
      );
      const userInfo = JSON.parse(
        globalThis.localStorage.getItem(authConfig.userInfo!) ?? ''
      );

      if (accessToken && userInfo) {
        dispatch({
          type: ACTION_TYPE_ENUM.INITIALIZE,
          payload: {
            isAuthenticated: true,
            userInfo
          }
        });
      } else {
        removeTokenInfo();
        dispatch({
          type: ACTION_TYPE_ENUM.INITIALIZE,
          payload: {
            isAuthenticated: false,
            userInfo: null
          }
        });
      }
    } catch (err) {
      dispatch({
        type: ACTION_TYPE_ENUM.INITIALIZE,
        payload: {
          isAuthenticated: false,
          userInfo: null
        }
      });
    }
  }, [dispatch]);

  useEffect(
    () => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const signIn = useCallback(
    async (capaAccessToken: string): Promise<void> => {
      const body: AutoLogin = { capaAccessToken, grant_type: 'token' };
      try {
        const res = await auto_login(body);
        const { result } = res.data;
        if (result.accessToken) {
          const decodeToken = jwt_decode(result.accessToken) as AccessToken;
          const userInfo: UserInfo = {
            role: decodeToken.roles,
            friendId: decodeToken.friendId
          };

          localStorage.setItem(authConfig.userInfo!, JSON.stringify(userInfo));
          localStorage.setItem(authConfig.accessTokenKey!, result.accessToken);
          localStorage.setItem(
            authConfig.refreshTokenKey!,
            result.refreshToken!
          );
          localStorage.removeItem(STORAGE_ACTION.UTM_CAMPAIGN);
          localStorage.removeItem(STORAGE_ACTION.UTM_MEDIUM);
          localStorage.removeItem(STORAGE_ACTION.UTM_SOURCE);

          dispatch({
            type: ACTION_TYPE_ENUM.SIGN_IN,
            payload: {
              userInfo
            }
          });
          window.location.href = paths.dashboard.index;
        } else {
          throw new Error();
        }
      } catch (e) {
        if (window.confirm('로그인에 실패하였습니다. 다시 시도해주세요.')) {
          window.location.href = `${process.env.REACT_APP_CAPA_URL}/account`;
        }
      }
    },
    [handleSnackBar]
  );

  const signOut = useCallback(async (): Promise<void> => {
    localStorage.removeItem(authConfig.userInfo!);
    localStorage.removeItem(authConfig.accessTokenKey!);
    localStorage.removeItem(authConfig.refreshTokenKey!);
    router.push(paths.login);
    dispatch({ type: ACTION_TYPE_ENUM.SIGN_OUT });
  }, [dispatch, router]);

  // const signUp = useCallback(
  //   async (body: RegisterInput, friendsType: USER_TYPE_ENUM): Promise<void> => {
  //     const res = await register(body);
  //     if (res.data.success) {
  //       handleSnackBar({
  //         severity: 'success',
  //         message:
  //           friendsType !== USER_TYPE_ENUM.PERSONAL
  //             ? '회원가입 신청이 완료되었습니다.'
  //             : '회원가입이 완료되었습니다.'
  //       });
  //       if (friendsType === USER_TYPE_ENUM.PERSONAL) {
  //         await signIn(body.email, body.password, null);
  //       } else {
  //         router.push(paths.index);
  //       }
  //       localStorage.removeItem(STORAGE_ACTION.UTM_CAMPAIGN);
  //       localStorage.removeItem(STORAGE_ACTION.UTM_MEDIUM);
  //       localStorage.removeItem(STORAGE_ACTION.UTM_SOURCE);
  //     }

  //     dispatch({ type: ACTION_TYPE_ENUM.SIGN_UP });
  //   },
  //   [handleSnackBar, router, signIn]
  // );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = AuthContext.Consumer;
